import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  FormControl,
  InputLabel, 
  Select,   
  MenuItem,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  styled,
  Badge,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Agriculture as FarmIcon,
  ShoppingCart as OrderIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import { H6 } from "components/Typography";
import { usePageTitle } from "contexts/PageTitleContext";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import AppPagination from "components/AppPagination";
import { Stack } from "@mui/material";

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    transform: 'translateY(-2px)'
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "16px 20px",
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  whiteSpace: "nowrap",
  "&.header": {
    fontWeight: 600,
    fontSize: "0.875rem",
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    color: theme.palette.text.primary,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
}));

const StatusChip = styled(Chip)(({ status, theme }) => {
  const isActive = status?.toLowerCase() === 'active';
  return {
    backgroundColor: isActive ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
    color: isActive ? theme.palette.success.dark : theme.palette.error.dark,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
  };
});

const SummaryCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100px',
    height: '100px',
    background: `radial-gradient(circle, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 70%)`,
    borderRadius: '50%',
    transform: 'translate(30px, -30px)',
  }
}));

const Farmers = () => {
  const theme = useTheme();
  const { setPageTitle } = usePageTitle();
  
  // State management
  const [allFarmers, setAllFarmers] = useState([]); // Store all farmers from API
  const [farmers, setFarmers] = useState([]); // Displayed farmers after filtering
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState(''); // For input field
  const [searchTerm, setSearchTerm] = useState(''); // For actual search trigger
  const [hasSearched, setHasSearched] = useState(false); // Added for search state management
  const [filteredFarmers, setFilteredFarmers] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState(''); // For API search parameter
  
  // Pagination state
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null
  });
  
  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalFarmers: 0,
    activeFarmers: 0,
    totalOrders: 0,
    totalFarms: 0
  });

  const isInitialMount = useRef(true);

  useEffect(() => {
    setPageTitle('Farmers Management');
    fetchFarmers(1, pagination.page_size, '');
  }, [setPageTitle]);

  // Fetch farmers from API with pagination and search
  const fetchFarmers = async (page = 1, pageSize = pagination.page_size, search = searchKeyword) => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });
      
      // Add search parameter if exists
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_farmers}?${params.toString()}`
      );
      
      if (response?.success) {
        // Parse farmers data
        const rawData = response.data;
        const farmersData = Array.isArray(rawData)
          ? rawData
          : rawData?.farmers || rawData?.data || [];
        
        // Update farmers for current page
        setFarmers(farmersData);
        
        // For search results, we don't store all farmers
        if (!search || !search.trim()) {
          setAllFarmers(farmersData);
          setFilteredFarmers(farmersData);
        } else {
          setFilteredFarmers(farmersData);
        }
        
        // Parse pagination from response
        const paginationData = response.pagination || response.data?.pagination || {};
        const totalFarmers = paginationData.total_items ?? farmersData.length;
        
        setPagination({
          current_page: paginationData.current_page ?? page,
          page_size: paginationData.page_size ?? pageSize,
          total_items: paginationData.total_items ?? totalFarmers,
          total_pages: paginationData.total_pages ?? Math.ceil(totalFarmers / pageSize),
          has_next: paginationData.has_next ?? false,
          has_previous: paginationData.has_previous ?? false,
          next_page_number: paginationData.next_page_number ?? null,
          previous_page_number: paginationData.previous_page_number ?? null
        });
        
        // Update summary stats only on initial load without search
        if (page === 1 && (!search || !search.trim())) {
          setSummaryStats({
        totalFarmers: totalFarmers,
        activeFarmers: response?.counts?.active_farmers ?? 0,
        totalOrders: farmersData.reduce((sum, farmer) => sum + (farmer.order_count || 0), 0),
        totalFarms: farmersData.reduce((sum, farmer) => sum + (farmer.farm_count || 0), 0)
      });
        }
      } else {
        throw new Error(response?.message || 'Failed to fetch farmers');
      }
    } catch (err) {
      console.error('Error fetching farmers:', err);
      // setError(err.message || 'Failed to load farmers data');
    } finally {
      setLoading(false);
    }
  };

  const onPageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    fetchFarmers(1, newSize, searchKeyword);
  };

  /* ----------------------------------------- */
  /* SEARCH HANDLERS - Server-side filtering    */
  /* ----------------------------------------- */

  const handleSearch = () => {
    if (!searchInput.trim()) {
      // If search is empty, clear search and fetch all
      if (searchKeyword) {
        setSearchKeyword('');
        fetchFarmers(1, pagination.page_size, '');
      }
      return;
    }
    
    setSearchTerm(searchInput);
    setHasSearched(true);
    setSearchKeyword(searchInput);
    
    // Fetch from API with search parameter
    fetchFarmers(1, pagination.page_size, searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setHasSearched(false);
    setSearchKeyword("");
    setFilteredFarmers(allFarmers);
    setPagination(prev => ({
      ...prev,
      current_page: 1,
      total_items: allFarmers.length,
      total_pages: Math.ceil(allFarmers.length / prev.page_size)
    }));
    // Fetch all farmers without search
    fetchFarmers(1, pagination.page_size, '');
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && searchInput.trim()) {
      handleSearch();
    }
  };

  const handlePageChange = (_, page) => {
    // Always use API pagination with current search keyword
    fetchFarmers(page, pagination.page_size, searchKeyword);
  };

  const handleRefresh = () => {
    // Clear search and refresh
    setSearchInput("");
    setSearchTerm("");
    setHasSearched(false);
    setSearchKeyword("");
    fetchFarmers(1, pagination.page_size, '');
  };

  const formatFullName = (farmer) => {
    return `${farmer.first_name || ''} ${farmer.last_name || ''}`.trim() || 'N/A';
  };

  const formatAddress = (farmer) => {
    const addressParts = [
      farmer.village,
      farmer.tehsil,
      farmer.district,
      farmer.state,
      farmer.country
    ].filter(Boolean);
    
    return addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
  };

  const getInitials = (farmer) => {
    const firstName = farmer.first_name || '';
    const lastName = farmer.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'F';
  };

  if (loading && farmers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading farmers data...</Typography>
      </Box>
    );
  }

  return (
    <Box pt={2} pb={4}>
      {/* Page Header */}
      {/* <FlexBetween mb={3}>
        <Box>
          <H6 fontSize={18} mb={0.5}></H6>
          <Typography variant="body2" color="text.secondary">
            Manage and view farmer information and statistics
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </FlexBetween> */}

      {/* Summary Cards */}
      {/* <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard>
            <FlexBetween>
              <Box>
                <Typography variant="h4" fontWeight={700} mb={0.5}>
                  {summaryStats.totalFarmers}
                </Typography>
                <Typography variant="body2" opacity={0.8}>
                  Total Farmers
                </Typography>
              </Box>
              <PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />
            </FlexBetween>
          </SummaryCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <FlexBetween>
              <Box>
                <Typography variant="h4" fontWeight={700} mb={0.5} color="success.dark">
                  {summaryStats.activeFarmers}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Farmers
                </Typography>
              </Box>
              <Badge badgeContent="Active" color="success"  sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  top:-10,
                  right:20
                },
              }}>
                <PersonIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Badge>
            </FlexBetween>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
            <FlexBetween>
              <Box>
                <Typography variant="h4" fontWeight={700} mb={0.5} color="info.dark">
                  {summaryStats.totalOrders}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Orders
                </Typography>
              </Box>
              <OrderIcon sx={{ fontSize: 40, color: 'info.main' }} />
            </FlexBetween>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
            <FlexBetween>
              <Box>
                <Typography variant="h4" fontWeight={700} mb={0.5} color="warning.dark">
                  {summaryStats.totalFarms}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Farms
                </Typography>
              </Box>
              <FarmIcon sx={{ fontSize: 40, color: 'warning.main' }} />
            </FlexBetween>
          </Card>
        </Grid>
      </Grid> */}

      {/* Search and Filters */}
      <StyledCard sx={{ p: 3, mb: 3 }}>
        <FlexBetween mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Farmers Directory
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </FlexBetween>

        {/* Search Field with Button */}
        <Grid container spacing={2} alignItems="center">
          {/* Search Input */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name, mobile....."
              value={searchInput}
              onChange={(e) => {
                const value = e.target.value;
                setSearchInput(value);
                // Re-enable search button when user modifies the text
                if (hasSearched && value !== searchTerm) {
                  setHasSearched(false);
                }
                // When user clears input manually, restore full data
                if (!value.trim() && (searchKeyword || searchTerm)) {
                  handleClearSearch();
                }
              }}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { height: 40 }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 40,
                  borderRadius: 2
                }
              }}
            />
          </Grid>
          
          {/* Search Button and Rows Dropdown */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* Search Button */}
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                disabled={!searchInput.trim() || (searchInput === searchTerm && hasSearched)}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: "none", 
                  height: 40,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none'
                  }
                }}
              >
                Search
              </Button>
              
              {/* Rows Dropdown */}
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel>Rows</InputLabel>
                <Select 
                  value={pagination.page_size} 
                  label="Rows" 
                  onChange={onPageSizeChange}
                >
                  {[10, 25, 50, 100].map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
        
        
        {/* Display active search term */}
        {/* {searchTerm && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
              Showing results for: "{searchTerm}"
            </Typography>
            {hasSearched && searchInput === searchTerm && (
              <Typography variant="caption" color="text.secondary">
                (Search active)
              </Typography>
            )}
            <Button 
              size="small" 
              onClick={handleClearSearch}
              sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}
            >
              Clear
            </Button>
          </Box>
        )} */}
      </StyledCard>
      
      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Farmers Table */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          maxHeight: 890,
          overflow: 'auto',
          padding: "0px 0px 9px 9px",
          /* Firefox */
          scrollbarWidth: "thin",
          scrollbarColor: theme.palette.mode === "dark" ? "#555 #1e1e1e" : "#c1c1c1 #f1f1f1",
          /* Chrome, Edge, Safari */
          "&::-webkit-scrollbar": {
            height: 8,
            width: 8,
          },
          "&::-webkit-scrollbar-track": {
            background: theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
          },
        }}
      >
        <Table>
          <TableHead sx={{ position: "sticky", top: "-8px", zIndex: 20 }}>
            <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
              <StyledTableCell 
                className="header" 
                style={{
                  paddingLeft: "18px", 
                  backgroundColor: theme.palette.grey[200],
                  width: '80px'
                }}
              >
                Sr. No.
              </StyledTableCell>
              <StyledTableCell 
                className="header" 
                style={{
                  backgroundColor: theme.palette.grey[200],
                }}
              >
                <Box sx={{ width: 150, textAlign: "center" }}>
                  Farmer
                </Box>
              </StyledTableCell>
              <StyledTableCell 
                className="header"
                style={{
                  backgroundColor: theme.palette.grey[200]
                }}
              >
                <Box sx={{ width: 150, textAlign: "center" }}>
                  Contact
                </Box>
              </StyledTableCell>
              <StyledTableCell 
                className="header"
                style={{
                  backgroundColor: theme.palette.grey[200]
                }}
              >
                <Box sx={{ width: 244, textAlign: "center" }}>
                  Location
                </Box>
              </StyledTableCell>
      <StyledTableCell 
        className="header"
        style={{
          backgroundColor: theme.palette.grey[200],
        }}
        >
                <Box sx={{ width: 150, textAlign: "center" }}>
                  Statistics
                </Box>
              </StyledTableCell>
              <StyledTableCell 
                className="header" 
                align="center" 
                sx={{ 
                  width: '150px',
                  position: 'sticky !important',
                  top: '0 !important',
                  right: 0,
                  zIndex: '11 !important',
                  backgroundColor: (theme) => `${theme.palette.grey[200]} !important`,
                  boxShadow: (theme) => `-2px 0 4px -2px ${theme.palette.divider}`
                }}
              >
                Status
              </StyledTableCell>

            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <StyledTableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} thickness={4} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading farmers...
                  </Typography>
                </StyledTableCell>
              </TableRow>
            ) : farmers.length === 0 ? (
              <TableRow>
                <StyledTableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm
                      ? `No farmers found matching "${searchTerm}"`
                      : 'No farmers data available'}
                  </Typography>
                  {searchTerm && (
                    <Button
                      variant="text"
                      onClick={handleClearSearch}
                      sx={{ mt: 1 }}
                    >
                      Clear search
                    </Button>
                  )}
                </StyledTableCell>
              </TableRow>
            ) : (
              farmers.map((farmer, index) => {
                // Calculate serial number based on current page and page size
                const serialNumber = (pagination.current_page - 1) * pagination.page_size + index + 1;
                
                return (
                  <TableRow
                    key={farmer.id}
                    hover
                    sx={{
                      transition: 'background 0.2s',
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.action.hover,
                      },
                    }}
                  >
                    {/* Sr. No. */}
                    <StyledTableCell style={{ paddingLeft: "16px" }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {serialNumber}
                      </Typography>
                    </StyledTableCell>
                    {/* Farmer info */}
                    <StyledTableCell style={{ paddingLeft: "16px" }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar
                          src={farmer.profile_image}
                          sx={{
                            width: 44,
                            height: 44,
                            bgcolor: (theme) => theme.palette.primary.main,
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#fff',
                          }}
                        >
                          {getInitials(farmer)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {formatFullName(farmer)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {farmer.id}
                          </Typography>
                        </Box>
                      </Box>
                    </StyledTableCell>

                    {/* Contact info */}
                    <StyledTableCell>
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {farmer.mobile_number || 'N/A'}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: 160 }}
                          >
                            {farmer.email || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </StyledTableCell>

                    {/* Location */}
                    <StyledTableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Box>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 220 }}
                          >
                            {formatAddress(farmer)}
                          </Typography>
                          {farmer.pincode && (
                            <Typography variant="caption" color="text.secondary">
                              PIN: {farmer.pincode}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </StyledTableCell>

                    {/* Stats */}
                    <StyledTableCell>
                      <Box display="flex" gap={1.5}>
                        <Chip
                          label={`${farmer.order_count || 0} Orders`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                        <Chip
                          label={`${farmer.farm_count || 0} Farms`}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Box>
                    </StyledTableCell>

                    {/* Status */}
                    <StyledTableCell 
                      sx={{ 
                        position: "sticky",
                        right: 0,
                        zIndex: 4,
                        backgroundColor: theme.palette.background.paper,
                        display: "flex", 
                        gap: 1,
                        minWidth: 200,
                        justifyContent: "center",
                        padding: "30px 20px"
                      }}
                    >
                      <StatusChip
                        label={farmer.status || 'Unknown'}
                        status={farmer.status}
                        size="small"
                      />
                    </StyledTableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
        {/* Showing text */}
        <Typography variant="body2" color="text.secondary">
          {pagination.total_items > 0
            ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of ${pagination.total_items}`
            : 'No farmers'}
        </Typography>
        {pagination.total_pages > 1 && (
          <Stack alignItems="center">
            <AppPagination
              shape="rounded"
              count={pagination.total_pages}
              page={pagination.current_page}
              onChange={handlePageChange}
            />
          </Stack>
        )}
      </Box>
     
    </Box>
  );
};

export default Farmers;